import React, { Fragment, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { selectConfirmed } from './selectors';
import { confirm } from './actions';
import { Button, PlainButton } from '../Controls';
import { Prompt, PromptText } from './styled';

/**
 * A button that asks before it acts. Tapping it opens a prompt bar at the bottom of the screen, so the question has
 * room even when the button is a small card on the board. Only one prompt is open at a time.
 *
 * `plain` buttons wrap a card or board spot; the card itself shows whether it can be chosen.
 */
const ConfirmButton = ({ onConfirm, onCancel, disabled, children, plain, primary, block, prompt, confirmLabel }) => {
  const dispatch = useDispatch();
  const confirmed = useSelector(selectConfirmed);
  const identifier = useRef(Symbol('CONFIRM_BUTTON_IDENTIFIER')).current;
  const open = confirmed === identifier;

  const handleConfirm = () => {
    dispatch(confirm(null));
    onConfirm();
  };

  const handleCancel = () => {
    dispatch(confirm(null));
    onCancel && onCancel();
  };

  // Escape answers the prompt with Cancel.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const cancelOnEscape = (event) => {
      if (event.key === 'Escape') {
        dispatch(confirm(null));
        onCancel && onCancel();
      }
    };
    document.addEventListener('keydown', cancelOnEscape);

    return () => document.removeEventListener('keydown', cancelOnEscape);
  }, [open, onCancel, dispatch]);

  const question = prompt || (typeof children === 'string' ? `${children}?` : 'Are you sure?');

  return (
    <Fragment>
      {plain ?
        // data-chosen: the card being asked about stands out from the other choices (Card/styled targetable).
        <PlainButton onClick={() => dispatch(confirm(identifier))} disabled={disabled || open} data-chosen={open || undefined}>
          {children}
        </PlainButton> :
        <Button
          onClick={() => dispatch(confirm(identifier))}
          disabled={disabled || open}
          $primary={primary}
          $block={block}
        >
          {children}
        </Button>}
      {open && createPortal(
        <Prompt role="dialog" aria-label={question}>
          <PromptText>{question}</PromptText>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleConfirm} $primary>{confirmLabel || 'Confirm'}</Button>
        </Prompt>,
        document.body,
      )}
    </Fragment>
  );
};

ConfirmButton.propTypes = {
  onConfirm: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
  plain: PropTypes.bool,
  primary: PropTypes.bool,
  block: PropTypes.bool,
  onCancel: PropTypes.func,
  prompt: PropTypes.string,
  confirmLabel: PropTypes.string,
};

ConfirmButton.defaultProps = {
  disabled: false,
};

export default ConfirmButton;
